import { FC, useState, useCallback, useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip, Cell } from 'recharts'
import { Tooltip as MuiTooltip } from '@mui/material'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import { useAppDispatch } from 'store/hooks'
import { openModal, ModalType } from 'store/modals/modalSlice'
import { BenchmarkScoreCategoryData } from 'models/Benchmark'
import { dateFormat } from 'lib/utils'
import theme from 'theme'
import { useThemeColors } from 'hooks/useThemeColors'
import CategoryPageSelectField from '../../components/CategoryPageSelectField'
import { BenchmarkingData, CPIPeriod } from '../../categoryDashboard.types'
import { BenchmarkingWrapper } from './benchmarkingSection.styles'
import { SkeletonBar } from '../meta-data/metaDataSection.styles'
import { BenchmarkingIcon, InfoIcon, LoadingSpinner } from '../icons'
import BenchmarkingChartTooltip from './BenchmarkingChartTooltip'

dayjs.extend(weekday)

const topBarTooltipProps = {
  tooltip: {
    sx: {
      padding: '16px',
      borderRadius: '12px',
      background: theme.colors.surfaceContainerBackground,
      border: `1px solid ${theme.colors.dropdownBorder}`,
      boxShadow: '0 4px 8px 0 rgba(30, 26, 29, 0.20)',
      fontFamily: 'Quicksand, sans-serif',
      maxWidth: 'none'
    }
  }
}

const renderSegmentTooltip = (value: number, label: string) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span style={{ fontSize: 20, lineHeight: '28px', fontWeight: 600, color: theme.colors.menuItemText }}>{value}%</span>
    <span style={{ fontSize: 12, lineHeight: '16px', fontWeight: 500, color: theme.colors.devicesSecondaryText }}>{label}</span>
  </div>
)

const PERIOD_OPTIONS = [CPIPeriod.OneMonth, CPIPeriod.ThreeMonths, CPIPeriod.SixMonths, CPIPeriod.OneYear]
interface Props {
  data?: BenchmarkingData
  selectedPeriod: CPIPeriod
  onTimePeriodChange: (period: CPIPeriod) => void
  industryOptions: string[]
  onIndustryChange: (industry: string) => void
  benchmarkCpis: BenchmarkScoreCategoryData[]
  loading?: boolean
  historyLoading?: boolean
  hasActiveCpis?: boolean
}

const BenchmarkingSection: FC<Props> = ({
  data,
  selectedPeriod,
  onTimePeriodChange,
  industryOptions,
  onIndustryChange,
  benchmarkCpis,
  loading,
  historyLoading,
  hasActiveCpis
}) => {
  const dispatch = useAppDispatch()
  const getColor = useThemeColors()
  const hasSummary = (data?.lessStrict ?? 0) + (data?.sameSLA ?? 0) + (data?.moreStrict ?? 0) > 0
  // When the API returns chart points but every bucket is 0/0/0 (no
  // historical data has been collected yet for this category), the chart
  // would render an empty axis with flat zero bars. Treat that as empty so
  // the proper "no data" state shows instead.
  const hasChartValues = (data?.chartData || []).some(
    (p) => (p.less ?? 0) + (p.same ?? 0) + (p.more ?? 0) > 0
  )
  const isEmpty = !hasActiveCpis || !data?.chartData || data.chartData.length === 0 || !hasChartValues
  const showTopBar = !loading && hasActiveCpis && hasSummary
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [activeTopSegment, setActiveTopSegment] = useState<'less' | 'same' | 'more' | null>(null)

  const topSegmentOpacity = (segment: 'less' | 'same' | 'more') =>
    activeTopSegment != null && activeTopSegment !== segment ? 0.3 : 1

  const historyLastUpdate = useMemo(() => {
    const points = data?.chartData || []
    if (points.length > 0) {
      const last = points[points.length - 1].fullDate
      if (last) {
        return dayjs(last).weekday(8).format(dateFormat.lastUpdate)
      }
    }
    return ''
  }, [data?.chartData])

  const chartTicks = useMemo(
    () => (data?.chartData || []).filter((p) => p.xLabel).map((p) => p.fullDate),
    [data?.chartData]
  )
  const xLabelByDate = useMemo(
    () => new Map((data?.chartData || []).map((p) => [p.fullDate, p.xLabel])),
    [data?.chartData]
  )

  const handleMouseMove = useCallback((state: any) => {
    if (state?.activeTooltipIndex != null) {
      setActiveIndex(state.activeTooltipIndex)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setActiveIndex(null)
  }, [])

  const handlePeriodChange = (e: any) => {
    onTimePeriodChange(e.target.value as CPIPeriod)
  }

  const handleIndustryChange = (e: any) => {
    onIndustryChange(e.target.value)
  }

  const handleOpenBenchmarkingDetails = () => {
    dispatch(
      openModal({
        type: ModalType.benchmarkingDetails,
        props: {
          benchmarkingDetailsData: {
            cpis: benchmarkCpis,
            lastUpdate: data?.lastUpdate ?? '',
            /* The same split the bar draws, so the two can't drift. */
            summary: {
              less: data?.lessStrict ?? 0,
              equal: data?.sameSLA ?? 0,
              more: data?.moreStrict ?? 0
            }
          }
        }
      })
    )
  }

  return (
    <BenchmarkingWrapper>
      <div className="benchmarking-top">
        <div className="top-content">
          <div className="header-row">
            <div className="header-left">
              <div className="title-row">
                <BenchmarkingIcon className="title-icon" />
                <span className="title-text">Benchmarking</span>
                {!loading && benchmarkCpis.length > 0 && (
                  <span className="info-icon-wrapper" onClick={handleOpenBenchmarkingDetails}>
                    <InfoIcon />
                  </span>
                )}
              </div>
              <div className="last-update">
                Last Update:{' '}
                {data?.lastUpdate ? (
                  <span className="date-value">{data.lastUpdate}</span>
                ) : (
                  <SkeletonBar
                    width="120px"
                    sx={{ height: '16px', display: 'inline-block', verticalAlign: 'middle' }}
                  />
                )}
              </div>
            </div>
            <CategoryPageSelectField
              options={loading || industryOptions.length === 0 ? ['All Industries'] : industryOptions}
              value={loading ? 'All Industries' : data?.industry || 'All Industries'}
              handleChange={handleIndustryChange}
              minWidth="158px"
              dropdownWidth="205px"
              anchorRight
              disabled={loading}
            />
          </div>

          {showTopBar && (
            <div className="stacked-bar-section">
              <div className="bar-labels">
                <span style={{ width: `${data?.lessStrict}%` }}>
                  {(data?.lessStrict ?? 0) >= 6 ? `${data?.lessStrict}%` : ''}
                </span>
                <span style={{ width: `${data?.sameSLA}%` }}>
                  {(data?.sameSLA ?? 0) >= 6 ? `${data?.sameSLA}%` : ''}
                </span>
                <span style={{ width: `${data?.moreStrict}%` }}>
                  {(data?.moreStrict ?? 0) >= 6 ? `${data?.moreStrict}%` : ''}
                </span>
              </div>
              <div className="stacked-bar">
                <MuiTooltip
                  title={renderSegmentTooltip(data?.lessStrict ?? 0, 'of companies have less strict SLAs')}
                  placement="top"
                  componentsProps={topBarTooltipProps}
                >
                  <div
                    className="bar-segment less-strict"
                    style={{ width: `${data?.lessStrict}%`, opacity: topSegmentOpacity('less') }}
                    onMouseEnter={() => setActiveTopSegment('less')}
                    onMouseLeave={() => setActiveTopSegment(null)}
                  />
                </MuiTooltip>
                <MuiTooltip
                  title={renderSegmentTooltip(data?.sameSLA ?? 0, 'of companies have similar SLAs')}
                  placement="top"
                  componentsProps={topBarTooltipProps}
                >
                  <div
                    className="bar-segment same-sla"
                    style={{ width: `${data?.sameSLA}%`, opacity: topSegmentOpacity('same') }}
                    onMouseEnter={() => setActiveTopSegment('same')}
                    onMouseLeave={() => setActiveTopSegment(null)}
                  />
                </MuiTooltip>
                <MuiTooltip
                  title={renderSegmentTooltip(data?.moreStrict ?? 0, 'of companies have more strict SLAs')}
                  placement="top"
                  componentsProps={topBarTooltipProps}
                >
                  <div
                    className="bar-segment more-strict"
                    style={{ width: `${data?.moreStrict}%`, opacity: topSegmentOpacity('more') }}
                    onMouseEnter={() => setActiveTopSegment('more')}
                    onMouseLeave={() => setActiveTopSegment(null)}
                  />
                </MuiTooltip>
              </div>
            </div>
          )}

          {!showTopBar && <div className="empty-bar" />}
        </div>
      </div>

      <div className="benchmarking-bottom">
        <div className="chart-controls">
          <CategoryPageSelectField
            options={PERIOD_OPTIONS}
            value={selectedPeriod}
            handleChange={handlePeriodChange}
            minWidth="158px"
            dropdownWidth="158px"
            disabled={loading || historyLoading}
          />
          {!loading && !historyLoading && !isEmpty && historyLastUpdate && (
            <div className="history-last-update">
              Last Update: <span className="date-value">{historyLastUpdate}</span>
            </div>
          )}
        </div>

        {loading || historyLoading ? (
          <div className="empty-state">
            <LoadingSpinner />
          </div>
        ) : isEmpty ? (
          <div className="empty-state">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M16 4V27M16 27C14.0373 27 12.1572 27.3534 10.4198 28M16 27C17.9627 27 19.8428 27.3534 21.5802 28M25 6.62786C22.0594 6.21401 19.0547 6 16 6C12.9453 6 9.94056 6.21401 7 6.62786M25 6.62786C26.3473 6.81747 27.681 7.04902 29 7.32116M25 6.62786L28.4938 20.9282C28.6563 21.5931 28.3523 22.2983 27.7082 22.53C26.8624 22.8342 25.9506 23 25 23C24.0494 23 23.1376 22.8342 22.2918 22.53C21.6477 22.2983 21.3437 21.5931 21.5062 20.9282L25 6.62786ZM3 7.32116C4.31896 7.04902 5.65274 6.81747 7 6.62786M7 6.62786L10.4938 20.9282C10.6563 21.5931 10.3523 22.2983 9.70818 22.53C8.8624 22.8342 7.95056 23 7 23C6.04944 23 5.1376 22.8342 4.29182 22.53C3.64774 22.2983 3.34373 21.5931 3.50618 20.9282L7 6.62786Z"
                stroke={getColor('dropdown-label')}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {!hasActiveCpis ? (
              <>
                <span className="empty-title">No Benchmarking Data Is Available Yet</span>
                <span className="empty-description">
                  Activate CPI on the platform to start comparing your performance with peers
                </span>
              </>
            ) : hasSummary ? (
              <>
                <span className="empty-title">No Benchmarking Data Is Available Yet</span>
                <span className="empty-description">
                  CPI is activated. The system is currently retrieving and aggregating data. Trend charts will appear
                  soon.
                </span>
              </>
            ) : (
              <>
                <span className="empty-title">No Benchmarking Data Is Available</span>
                <span className="empty-description">No benchmark data available for this CPI category</span>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.chartData}
                  margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                  barGap={0}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  <XAxis
                    dataKey="fullDate"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: getColor('chart-axis-text'), fontSize: 12, fontFamily: 'Quicksand, sans-serif' }}
                    ticks={chartTicks}
                    interval={0}
                    tickFormatter={(value) => xLabelByDate.get(value) ?? ''}
                  />
                  <YAxis
                    domain={[0, 100]}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: getColor('chart-axis-text'), fontSize: 14, fontFamily: 'Quicksand, sans-serif' }}
                    tickFormatter={(value) => `${value}%`}
                    ticks={[0, 50, 100]}
                    width={46}
                  />
                  <ReferenceLine y={100} stroke={getColor('chart-grid')} strokeDasharray="8 4" strokeOpacity={0.5} />
                  <ReferenceLine y={50} stroke={getColor('chart-grid')} strokeDasharray="8 4" strokeOpacity={0.5} />
                  <Tooltip content={<BenchmarkingChartTooltip />} cursor={false} wrapperStyle={{ border: 'none', outline: 'none' }} />
                  <Bar dataKey="less" stackId="a" radius={[0, 0, 4, 4]}>
                    {data.chartData.map((_, i) => (
                      <Cell key={i} fill={getColor('chart-series-1')} opacity={activeIndex != null && activeIndex !== i ? 0.3 : 1} />
                    ))}
                  </Bar>
                  <Bar dataKey="same" stackId="a">
                    {data.chartData.map((_, i) => (
                      <Cell key={i} fill={getColor('chart-series-2')} opacity={activeIndex != null && activeIndex !== i ? 0.3 : 1} />
                    ))}
                  </Bar>
                  <Bar dataKey="more" stackId="a" radius={[4, 4, 0, 0]}>
                    {data.chartData.map((_, i) => (
                      <Cell key={i} fill={getColor('bench-more-strict')} opacity={activeIndex != null && activeIndex !== i ? 0.3 : 1} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="legend">
              <div className="legend-item">
                <div className="legend-color less-strict" />
                <span className="legend-label">Less strict SLA</span>
              </div>
              <div className="legend-item">
                <div className="legend-color same-sla" />
                <span className="legend-label">Same SLA</span>
              </div>
              <div className="legend-item">
                <div className="legend-color more-strict" />
                <span className="legend-label">More Strict SLA</span>
              </div>
            </div>
          </>
        )}
      </div>
    </BenchmarkingWrapper>
  )
}

export default BenchmarkingSection
